import { jsx } from "react/jsx-runtime";
import { createRef, forwardRef, useContext, useMemo, Children, isValidElement } from "react";
import PropTypes from "prop-types";
import { useMenuButtonContext, MenuItems } from "@reach/menu-button";
import { composeEventHandlers } from "@reach/utils";
import { AnimatedPopover } from "../animated-popover/animated-popover.es.js";
import { DropdownMenuListInner, DropdownMenuItems } from "./dropdown-menu-list.styles.es.js";
import { Lightbox } from "../lightbox/lightbox.es.js";
import { dropdownMenuContext } from "./dropdown-menu-context.es.js";
import { DropdownMenuPopover } from "./dropdown-menu-popover.es.js";
import { tabbableNext } from "../../utils/tabbable-next.es.js";
const propTypes = {
  alwaysExpanded: PropTypes.bool,
  children: PropTypes.node.isRequired,
  grouped: PropTypes.bool,
  maxHeight: PropTypes.number,
  minWidth: PropTypes.number,
  offset: PropTypes.number,
  onKeyDown: PropTypes.func,
  targetRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }),
  width: PropTypes.number
};
const defaultProps = {
  alwaysExpanded: false,
  grouped: false,
  maxHeight: void 0,
  minWidth: void 0,
  offset: 4,
  onKeyDown: void 0,
  targetRef: createRef(),
  width: 208
};
const DropdownMenuList = forwardRef(
  ({
    alwaysExpanded,
    children,
    grouped,
    maxHeight,
    minWidth,
    offset,
    onKeyDown,
    targetRef,
    width,
    ...restProps
  }, ref) => {
    const { appearance, buttonRef, justify, placement, spacing } = useContext(dropdownMenuContext);
    const { isExpanded } = useMenuButtonContext();
    const handleKeyDown = useMemo(
      () => composeEventHandlers(onKeyDown, (event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
        } else if (event.shiftKey && event.key === "Tab") {
          event.preventDefault();
          buttonRef.current.focus();
        } else if (event.key === "Tab") {
          const nextNode = tabbableNext(targetRef.current);
          if (nextNode) {
            event.preventDefault();
            nextNode.focus();
          }
        }
      }),
      [buttonRef, onKeyDown, targetRef]
    );
    return /* @__PURE__ */ jsx(
      AnimatedPopover,
      {
        animationAxis: placement === "left" || placement === "right" ? "both" : "y",
        as: DropdownMenuPopover,
        justify,
        offset,
        open: isExpanded || alwaysExpanded,
        placement,
        targetRef: buttonRef,
        children: grouped ? Children.map(
          children,
          (child) => isValidElement(child) ? /* @__PURE__ */ jsx(
            Lightbox,
            {
              grouped,
              appearance,
              children: /* @__PURE__ */ jsx(
                DropdownMenuListInner,
                {
                  maxHeight,
                  minWidth,
                  spacing,
                  width,
                  children: /* @__PURE__ */ jsx(
                    DropdownMenuItems,
                    {
                      as: MenuItems,
                      ref,
                      onKeyDown: handleKeyDown,
                      ...restProps,
                      children: child
                    }
                  )
                }
              )
            },
            child.key
          ) : child
        ) : /* @__PURE__ */ jsx(Lightbox, { grouped, appearance, children: /* @__PURE__ */ jsx(
          DropdownMenuListInner,
          {
            maxHeight,
            minWidth,
            spacing,
            width,
            children: /* @__PURE__ */ jsx(
              DropdownMenuItems,
              {
                as: MenuItems,
                ref,
                onKeyDown: handleKeyDown,
                ...restProps,
                children
              }
            )
          }
        ) })
      }
    );
  }
);
DropdownMenuList.propTypes = propTypes;
DropdownMenuList.defaultProps = defaultProps;
export {
  DropdownMenuList
};
